import useAuth from '@app/auth/use-auth';
import { zodResolver } from '@hookform/resolvers/zod';
import { useGetSites } from '@shared/api';
import {
  useCreateFuelUsageBusinessTravelActivity,
  useCreatePassengerTransportBusinessTravelActivity,
  useCreateTotalEmissionsBusinessTravelActivity,
  useDeleteBusinessTravelActivity,
  useGetBusinessTravelActivity,
  useListPassengerTransportModes,
  useUpdateFuelUsageBusinessTravelActivity,
  useUpdatePassengerTransportBusinessTravelActivity,
  useUpdateTotalEmissionsBusinessTravelActivity,
} from '@shared/api/hooks/ccf/activities/business-travel';
import { useGetFuels } from '@shared/api/hooks/ccf/fuels';
import { useGetCompanyReportPeriods } from '@shared/api/hooks/ccf/periods';
import { useGetActivityTypeUnits } from '@shared/api/hooks/ccf/units';
import {
  _RowUpdate,
  BusinessTravelRow,
  PassengerTransportMode,
  TimePeriod,
} from '@shared/api/types';
import Text from '@shared/components/content/text';
import SelectFormField from '@shared/components/form/select-form-field';
import SelectSearchFormField from '@shared/components/form/select-search-form-field';
import Loader from '@shared/components/loader';
import SimpleSelect from '@shared/components/simple-select';
import { Button } from '@shared/components/ui/button';
import { Form } from '@shared/components/ui/form';
import { useToast } from '@shared/components/ui/use-toast';
import sentry from '@shared/services/sentry';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { filteredTimePeriodEnum } from '../../../../options/time-period-enum';
import { useCCF } from '../../../ccf-context';
import { useFormattedSites, useGetRowTimePeriods } from '../../../utils';
import {
  BUSINESS_TRAVEL_METHODOLOGY,
  BusinessTravelMethodology,
  METHODOLOGY_DESCRIPTIONS,
  METHODOLOGY_LABELS,
  SECR_HELPER_TEXT,
  SECR_INCLUSION,
  SECR_INCLUSION_OPTIONS,
} from './constants';
import {
  BusinessTravelFormValues,
  DEFAULT_FORM_VALUES,
  DistanceValues,
  FuelUsageValues,
  RecordValues,
  TotalEmissionsValues,
  businessTravelFormSchema,
  isMethodologyValue,
} from './business-travel-form-schema';
import DistanceFields from './fields/distance-fields';
import FuelUsageFields from './fields/fuel-usage-fields';
import TotalEmissionsFields from './fields/total-emissions-fields';
import MethodologySelect from './methodology-select';

interface BusinessTravelEditFormProps {
  activityUuid?: string;
  closeDialog: () => void;
}

const TIME_PERIOD_DEFAULT = 'Year';

const buildCreateRecords = (
  records: RecordValues[],
  unit: string
): BusinessTravelRow[] =>
  records.map((r) => ({
    quantity: String(r.quantity),
    unit,
    timePeriod: r.timePeriod,
  }));

const buildUpdateRecords = (
  records: RecordValues[],
  unit: string
): _RowUpdate[] =>
  records.map((r) => ({
    uuid: r.uuid ?? '',
    quantity: r.quantity,
    unit,
    timePeriod: r.timePeriod,
  }));

const buildInitialRecords = (timePeriods: TimePeriod[]): RecordValues[] =>
  timePeriods.map((tp) => ({
    uuid: '',
    quantity: undefined as unknown as number,
    timePeriod: tp.uuid,
  }));

export default function BusinessTravelEditForm({
  activityUuid,
  closeDialog,
}: BusinessTravelEditFormProps) {
  const { uuid: reportUuid } = useCCF();
  const { user } = useAuth();
  const hasSecrFeature = !!user?.features?.secr;
  const defaultSecrInclusion = hasSecrFeature
    ? SECR_INCLUSION.include
    : SECR_INCLUSION.exclude;
  const { toast } = useToast();
  const isEditMode = !!activityUuid;
  const [timePeriod, setTimePeriod] = useState<string>(TIME_PERIOD_DEFAULT);

  const { mutateAsync: createFuelUsage, isLoading: isCreatingFuel } =
    useCreateFuelUsageBusinessTravelActivity();
  const { mutateAsync: updateFuelUsage, isLoading: isUpdatingFuel } =
    useUpdateFuelUsageBusinessTravelActivity();
  const {
    mutateAsync: createPassengerTransport,
    isLoading: isCreatingDistance,
  } = useCreatePassengerTransportBusinessTravelActivity();
  const {
    mutateAsync: updatePassengerTransport,
    isLoading: isUpdatingDistance,
  } = useUpdatePassengerTransportBusinessTravelActivity();
  const { mutateAsync: createTotalEmissions, isLoading: isCreatingTotal } =
    useCreateTotalEmissionsBusinessTravelActivity();
  const { mutateAsync: updateTotalEmissions, isLoading: isUpdatingTotal } =
    useUpdateTotalEmissionsBusinessTravelActivity();
  const { mutateAsync: deleteActivity, isLoading: isDeleting } =
    useDeleteBusinessTravelActivity();

  const isSubmitting =
    isCreatingFuel ||
    isUpdatingFuel ||
    isCreatingDistance ||
    isUpdatingDistance ||
    isCreatingTotal ||
    isUpdatingTotal;

  const { data: activity, isLoading: isActivityLoading } =
    useGetBusinessTravelActivity({
      reportUuid: reportUuid!,
      activityUuid,
    });

  const { data: periodOptions, isLoading: isPeriodsLoading } =
    useGetCompanyReportPeriods(reportUuid!);

  // Lift the lookups that the child fields/row depend on so we can gate the
  // form on them — otherwise the form renders before the activity-type units
  // arrive and the Unit trigger flashes blank. Params come from `activity`
  // (not form watches) because the reset that populates the form runs in an
  // effect after the first render. Same query keys as the children → cache
  // hits when they mount.
  const activitySite = activity?.site?.uuid;
  const activityFuel = activity?.fuel?.uuid;
  const activityPassengerTransportMode = (
    activity?.passengerTransportMode as PassengerTransportMode | undefined
  )?.uuid;
  const activityMethodology = activity?.inputMethodology;
  const isFuelUsageEdit =
    isEditMode && activityMethodology === BUSINESS_TRAVEL_METHODOLOGY.fuelUsage;
  const isDistanceEdit =
    isEditMode &&
    activityMethodology === BUSINESS_TRAVEL_METHODOLOGY.passengerTransport;

  const { isLoading: isFuelsLoading } = useGetFuels(
    { site: activitySite, include_travel: true },
    { enabled: isFuelUsageEdit }
  );
  const { isLoading: isModesLoading } = useListPassengerTransportModes(
    { site: activitySite },
    { enabled: isDistanceEdit }
  );
  const { isLoading: isUnitsLoading } = useGetActivityTypeUnits(
    'business-travel',
    isFuelUsageEdit
      ? { site: activitySite, fuel: activityFuel }
      : {
          site: activitySite,
          passenger_transport_mode: activityPassengerTransportMode,
        },
    { enabled: isFuelUsageEdit || isDistanceEdit }
  );

  const form = useForm<BusinessTravelFormValues>({
    resolver: zodResolver(businessTravelFormSchema),
    defaultValues: {
      ...DEFAULT_FORM_VALUES,
      includeSecr: defaultSecrInclusion,
    },
  });

  const { getValues, reset, handleSubmit, watch } = form;
  const methodology = watch('methodology');
  const watchedSecrInclusion = watch('includeSecr');
  const shouldFilterSecr =
    hasSecrFeature && watchedSecrInclusion === SECR_INCLUSION.include;

  const { data: sites, isLoading: isSitesLoading } = useGetSites({
    page: 1,
    page_size: 1000,
    ...(shouldFilterSecr ? { uk_only: true } : {}),
  });
  const formattedSites = useFormattedSites(sites?.results || []);

  const rowTimePeriods = useGetRowTimePeriods(timePeriod, periodOptions!);

  // Effect 1: populate records when methodology/time-period changes in add mode
  useEffect(() => {
    if (isEditMode) return;
    const current = getValues();
    if (
      current.methodology !== BUSINESS_TRAVEL_METHODOLOGY.fuelUsage &&
      current.methodology !== BUSINESS_TRAVEL_METHODOLOGY.passengerTransport
    ) {
      return;
    }
    if (!rowTimePeriods?.length) return;

    reset({
      ...current,
      records: buildInitialRecords(rowTimePeriods),
    });
  }, [rowTimePeriods, timePeriod, getValues, reset, isEditMode]);

  // Effect 2: populate form from the fetched activity in edit mode
  useEffect(() => {
    if (!activity) return;
    const inputMethodology = activity.inputMethodology;
    if (!isMethodologyValue(inputMethodology ?? undefined)) return;

    const sharedFields = {
      site: activity.site?.uuid,
    };
    const includeSecrValue = activity.includeSecr
      ? SECR_INCLUSION.include
      : SECR_INCLUSION.exclude;

    const activityRecords =
      (activity.records as
        | Array<{
            uuid: string;
            quantity: number;
            timePeriod: { uuid: string } | string;
          }>
        | undefined) ?? [];
    const records: RecordValues[] = activityRecords.map((record) => ({
      uuid: record.uuid,
      quantity: record.quantity,
      timePeriod:
        typeof record.timePeriod === 'string'
          ? record.timePeriod
          : record.timePeriod?.uuid,
    }));

    if (inputMethodology === BUSINESS_TRAVEL_METHODOLOGY.fuelUsage) {
      reset({
        ...sharedFields,
        methodology: BUSINESS_TRAVEL_METHODOLOGY.fuelUsage,
        includeSecr: includeSecrValue,
        fuel: activity.fuel?.uuid ?? '',
        unit: activity.unit?.uuid ?? '',
        records,
      });
    } else if (
      inputMethodology === BUSINESS_TRAVEL_METHODOLOGY.passengerTransport
    ) {
      const mode = activity.passengerTransportMode as
        | PassengerTransportMode
        | undefined;
      reset({
        ...sharedFields,
        methodology: BUSINESS_TRAVEL_METHODOLOGY.passengerTransport,
        includeSecr: includeSecrValue,
        vehicleType: mode?.mode ?? '',
        vehicleSize: mode?.type ?? undefined,
        fuelType: mode?.subtype ?? undefined,
        passengerTransportMode: mode?.uuid ?? '',
        unit: activity.unit?.uuid ?? '',
        records,
      });
    } else if (
      inputMethodology === BUSINESS_TRAVEL_METHODOLOGY.totalEmissions
    ) {
      reset({
        ...sharedFields,
        methodology: BUSINESS_TRAVEL_METHODOLOGY.totalEmissions,
        description: activity.description ?? '',
        emissions: activity.emissions ?? 0,
        includeSecr: SECR_INCLUSION.notApplicable,
      });
    }
  }, [activity, reset]);

  const handleMethodologyChange = (next: string) => {
    if (!isMethodologyValue(next)) return;
    const preservedSite = getValues('site');
    const currentSecr = getValues('includeSecr');
    const preservedSecr =
      currentSecr === SECR_INCLUSION.notApplicable
        ? defaultSecrInclusion
        : currentSecr;
    const newRecords = rowTimePeriods
      ? buildInitialRecords(rowTimePeriods)
      : [];

    if (next === BUSINESS_TRAVEL_METHODOLOGY.fuelUsage) {
      reset({
        methodology: BUSINESS_TRAVEL_METHODOLOGY.fuelUsage,
        site: preservedSite,
        includeSecr: preservedSecr,
        fuel: '',
        unit: '',
        records: newRecords,
      });
    } else if (next === BUSINESS_TRAVEL_METHODOLOGY.passengerTransport) {
      reset({
        methodology: BUSINESS_TRAVEL_METHODOLOGY.passengerTransport,
        site: preservedSite,
        includeSecr: preservedSecr,
        vehicleType: '',
        passengerTransportMode: '',
        unit: '',
        records: newRecords,
      });
    } else {
      reset({
        methodology: BUSINESS_TRAVEL_METHODOLOGY.totalEmissions,
        site: preservedSite,
        includeSecr: SECR_INCLUSION.notApplicable,
        description: '',
        emissions: undefined,
      });
    }
  };

  const submitFuelUsage = async (data: FuelUsageValues) => {
    const includeSecr = data.includeSecr === SECR_INCLUSION.include;
    if (isEditMode) {
      await updateFuelUsage({
        companyReportUuid: reportUuid!,
        activityUuid: activityUuid!,
        data: {
          records: buildUpdateRecords(data.records, data.unit),
          includeSecr,
        },
      });
    } else {
      await createFuelUsage({
        companyReportUuid: reportUuid!,
        data: {
          site: data.site || null,
          fuel: data.fuel,
          unit: data.unit,
          records: buildCreateRecords(data.records, data.unit),
          includeSecr,
        },
      });
    }
  };

  const submitDistance = async (data: DistanceValues) => {
    const includeSecr = data.includeSecr === SECR_INCLUSION.include;
    if (isEditMode) {
      await updatePassengerTransport({
        companyReportUuid: reportUuid!,
        activityUuid: activityUuid!,
        data: {
          records: buildUpdateRecords(data.records, data.unit),
          includeSecr,
        },
      });
    } else {
      await createPassengerTransport({
        companyReportUuid: reportUuid!,
        data: {
          site: data.site || null,
          passengerTransportMode: data.passengerTransportMode,
          unit: data.unit,
          records: buildCreateRecords(data.records, data.unit),
          includeSecr,
        },
      });
    }
  };

  const submitTotalEmissions = async (data: TotalEmissionsValues) => {
    const payload = {
      site: data.site || null,
      description: data.description,
      emissions: data.emissions,
      includeSecr: false,
    };
    if (isEditMode) {
      await updateTotalEmissions({
        companyReportUuid: reportUuid!,
        activityUuid: activityUuid!,
        data: payload,
      });
    } else {
      await createTotalEmissions({
        companyReportUuid: reportUuid!,
        data: payload,
      });
    }
  };

  const onSubmit = async (data: BusinessTravelFormValues) => {
    try {
      if (data.methodology === BUSINESS_TRAVEL_METHODOLOGY.fuelUsage) {
        await submitFuelUsage(data);
      } else if (
        data.methodology === BUSINESS_TRAVEL_METHODOLOGY.passengerTransport
      ) {
        await submitDistance(data);
      } else {
        await submitTotalEmissions(data);
      }
      toast({
        title: `${isEditMode ? 'Updated' : 'Added'} business travel data`,
        variant: 'success',
      });
      closeDialog();
    } catch (error) {
      sentry.log(error);
      toast({
        title: `Failed to ${isEditMode ? 'update' : 'add'} business travel data`,
        variant: 'destructive',
      });
    }
  };

  const handleDelete = async () => {
    if (!activityUuid) return;
    try {
      await deleteActivity({
        companyReportUuid: reportUuid!,
        activityUuid,
      });
      toast({ title: 'Deleted business travel data', variant: 'success' });
      closeDialog();
    } catch (error) {
      sentry.log(error);
      toast({
        title: 'Failed to delete business travel data',
        variant: 'destructive',
      });
    }
  };

  if (
    isSitesLoading ||
    isPeriodsLoading ||
    (isEditMode &&
      (isActivityLoading ||
        (isFuelUsageEdit && (isFuelsLoading || isUnitsLoading)) ||
        (isDistanceEdit && (isModesLoading || isUnitsLoading))))
  ) {
    return <Loader />;
  }

  const isTotalEmissions =
    methodology === BUSINESS_TRAVEL_METHODOLOGY.totalEmissions;
  const showTimePeriodSelect = !isEditMode && !isTotalEmissions;
  const secrHelperKey: BusinessTravelMethodology = isMethodologyValue(
    methodology
  )
    ? methodology
    : BUSINESS_TRAVEL_METHODOLOGY.fuelUsage;

  return (
    <Form {...form}>
      <form className="space-y-6" onSubmit={handleSubmit(onSubmit)}>
        {methodology && (
          <Text variant="subtle" className="-mt-4 text-sm">
            {METHODOLOGY_LABELS[methodology]} —{' '}
            {METHODOLOGY_DESCRIPTIONS[methodology]}
          </Text>
        )}
        <div className="grid grid-cols-12 gap-2">
          {hasSecrFeature && (
            <SelectFormField
              name="includeSecr"
              label="Include in SECR?"
              options={SECR_INCLUSION_OPTIONS}
              className="col-span-3"
              disabled={isEditMode || isTotalEmissions}
              tooltip={<>{SECR_HELPER_TEXT[secrHelperKey]}</>}
            />
          )}
          <div className="col-span-3">
            <MethodologySelect
              disabled={isEditMode}
              disableTotalEmissions={shouldFilterSecr}
              onMethodologyChange={handleMethodologyChange}
            />
          </div>
          <SelectSearchFormField
            name="site"
            pluralName="sites"
            label="Site"
            options={formattedSites}
            className="col-span-3"
            disabled={isEditMode}
            optional
          />

          {showTimePeriodSelect && (
            <SimpleSelect
              options={filteredTimePeriodEnum}
              value={timePeriod}
              onValueChange={setTimePeriod}
              className="col-span-3"
              label="Time period"
            />
          )}
        </div>

        {methodology === BUSINESS_TRAVEL_METHODOLOGY.fuelUsage && (
          <FuelUsageFields isEditMode={isEditMode} />
        )}
        {methodology === BUSINESS_TRAVEL_METHODOLOGY.passengerTransport && (
          <DistanceFields
            isEditMode={isEditMode}
            secrEligibleOnly={!isEditMode && shouldFilterSecr}
          />
        )}
        {isTotalEmissions && <TotalEmissionsFields isEditMode={isEditMode} />}

        <div className="mt-12 flex justify-between">
          <div>
            {isEditMode && (
              <Button
                type="button"
                size="lg"
                variant="outline-destructive"
                loading={isDeleting}
                disabled={isSubmitting}
                onClick={handleDelete}
              >
                Delete business travel data
              </Button>
            )}
          </div>
          <div className="flex space-x-2">
            <Button
              type="button"
              size="lg"
              variant="secondary"
              onClick={() => {
                closeDialog();
                reset();
              }}
              disabled={isSubmitting || isDeleting}
            >
              Cancel
            </Button>
            <Button
              type="submit"
              size="lg"
              loading={isSubmitting}
              disabled={isSubmitting || isDeleting}
            >
              {isEditMode ? 'Update' : 'Add'}
            </Button>
          </div>
        </div>
      </form>
    </Form>
  );
}
